// src/pages/RegisterPage.tsx (Refactored)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, UserPlus, Sparkles, Users, Globe, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/TranslationProvider';
import { AuthLayout } from '@/layouts/AuthLayout/AuthLayout';
import { Input } from '@/layouts/AuthLayout/ui/Input';
import { Button } from '@/layouts/AuthLayout/ui/Button';
import { Divider } from '@/layouts/AuthLayout/ui/Divider';
import { Alert } from '@/layouts/AuthLayout/ui/Alert';
import { SocialButton } from '@/layouts/AuthLayout/ui/SocialButton';
import { PasswordStrength } from '@/layouts/AuthLayout/ui/PasswordStrength';

interface RegisterPayload {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  gender?: string;
  phone?: string;
  country?: string;
}

const RegisterPage: React.FC = () => {
  const { t } = useLang();
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<RegisterPayload>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    display_name: '',
    gender: '',
    phone: '',
    country: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Auto-generate display_name
  useEffect(() => {
    if (!formData.display_name && (formData.first_name || formData.last_name)) {
      const generatedDisplayName = `${formData.first_name} ${formData.last_name}`.trim();
      if (generatedDisplayName && generatedDisplayName !== ' ') {
        setFormData(prev => ({ ...prev, display_name: generatedDisplayName }));
      }
    }
  }, [formData.first_name, formData.last_name]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!formData.first_name || !formData.last_name) {
      setError('First name and last name are required');
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const payload: RegisterPayload = {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        ...(formData.display_name && { display_name: formData.display_name }),
        ...(formData.gender && { gender: formData.gender }),
        ...(formData.phone && { phone: formData.phone }),
        ...(formData.country && { country: formData.country })
      };

      const response = await register(payload) as any;
      
      setSuccess('Account created successfully!');
      
      if (response?.tokens) {
        localStorage.setItem('access_token', response.tokens.access_token);
        localStorage.setItem('refresh_token', response.tokens.refresh_token);
        localStorage.setItem('token_expires_at', (Date.now() + response.tokens.expires_in * 1000).toString());
        window.dispatchEvent(new Event('auth-storage-changed'));
      }
      
      setTimeout(() => navigate('/home'), 2000);
    } catch (err: any) {
      const errorMessage = err.message || err.response?.data?.error || 'Registration failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Google registration failed');
      setLoading(false);
    }
  };

  return (
    <AuthLayout 
      title={t("Create Account")}
      subtitle="Join us and start your journey"
    >
      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      <SocialButton provider="google" onClick={handleGoogleRegister} loading={loading} />
      <Divider text={t("or")} />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            name="first_name"
            label={t("First Name")}
            icon={<Users className="w-4 h-4" />}
            required
            value={formData.first_name}
            onChange={handleChange}
            placeholder="John"
          />
          <Input
            name="last_name"
            label={t("Last Name")}
            icon={<Users className="w-4 h-4" />}
            required
            value={formData.last_name}
            onChange={handleChange}
            placeholder="Doe"
          />
        </div>

        <Input
          name="display_name"
          label={t("Display Name")}
          icon={<User className="w-4 h-4" />}
          optional
          value={formData.display_name}
          onChange={handleChange}
          placeholder="Auto-generated from first/last name"
          hint="Leave empty to auto-generate from first and last name"
        />

        <Input
          type="email"
          name="email"
          label={t("Email")}
          icon={<Mail className="w-4 h-4" />}
          required
          value={formData.email}
          onChange={handleChange}
          placeholder="you@example.com"
        />

        <div>
          <Input
            type="password"
            name="password"
            label={t("Password")}
            icon={<Lock className="w-4 h-4" />}
            required
            value={formData.password}
            onChange={handleChange}
            placeholder="••••••••"
          />
          <PasswordStrength password={formData.password} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            name="phone"
            label={t("Phone")}
            icon={<Phone className="w-4 h-4" />}
            optional
            value={formData.phone}
            onChange={handleChange}
            placeholder="+1234567890"
          />
          <Input
            name="country"
            label={t("Country")}
            icon={<Globe className="w-4 h-4" />}
            optional
            value={formData.country}
            onChange={handleChange}
            placeholder="United States"
          />
        </div>

        <Input
          name="gender"
          label={t("Gender")}
          icon={<Users className="w-4 h-4" />}
          optional
          value={formData.gender}
          onChange={handleChange}
          placeholder="Select gender"
        />

        <div className="flex items-start gap-3 pt-2">
          <input
            type="checkbox"
            id="terms"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          <label htmlFor="terms" className="text-sm text-gray-600 dark:text-gray-400">
            I agree to the{' '}
            <Link to="/terms" className="text-purple-600 dark:text-purple-400 hover:underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-purple-600 dark:text-purple-400 hover:underline">
              Privacy Policy
            </Link>
          </label>
        </div>

        <Button
          type="submit"
          loading={loading}
          icon={<UserPlus className="w-5 h-5" />}
          fullWidth
          disabled={!termsAccepted}
        >
          {t("Register")}
        </Button>
      </form>

      <Divider />

      <div className="text-center">
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-purple-600 dark:text-purple-400 font-semibold hover:underline inline-flex items-center gap-1">
            Sign in
            <Sparkles className="w-3 h-3" />
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;